import { addWishlistItemAction } from "@/actions/wishlist";
import { useMarket } from "@app/_context";
import type { CustomProduct } from "@apptus/esales-api";
import useAuth from "@features/auth/hooks/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { addWishlistItemToLocalStorage } from "../utils/localWishlistStorage";

export default function useAddWishlistItem() {
  const { market } = useMarket().state;
  const { token, user } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: async (product: CustomProduct) => {
      if (!token) {
        if (user) return;
        const productId = product.key;
        const variantId = product.variants[0]?.key;
        if (!productId || !variantId) return;
        return addWishlistItemToLocalStorage({ productId, variantId });
      }

      const [variant] = product.variants;

      if (!variant) {
        console.error("useAddWishlistItem - variant is not defined");
        return;
      }

      return addWishlistItemAction({
        token,
        productId: product.key,
        variantId: variant.key,
        locale: market.serviceLocale,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      void queryClient.refetchQueries({ queryKey: ["wishlist"] });
    },
  });

  const addWishlistItem = useCallback(
    (product: CustomProduct) => {
      mutate(product);
    },
    [mutate],
  );
  const addWishlistItemAsync = useCallback(
    (product: CustomProduct) => {
      return mutateAsync(product);
    },
    [mutateAsync],
  );

  return {
    addWishlistItem,
    addWishlistItemAsync,
    isPending,
    isSuccess,
  };
}
